import {
  Card,
  GhostButton,
  Icon,
  PrimaryButton,
  Text,
  LegacyMenuSection,
  LegacyMenuItem,
  useColors,
} from "@certn/cascadia-react";
import { GhostIconButton } from "@certn/cascadia-react/components/button/GhostIconButton";
import { LegacyDropdownMenu } from "@certn/cascadia-react/deprecated/dropdown-menu/LegacyDropdownMenu";
import { CascadiaColor } from "@certn/cascadia-tokens";
import { useFutureTimeDifference, usePastTimeDifference } from "@certn/utils";
import { css } from "@emotion/css";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { DeclineModal } from "./DeclineModal";
import { ExpirationBar } from "./ExpirationBar";
import { useCardStyles } from "./styles";
import { CheckStatus } from "../CheckStatus/CheckStatus";

type CardProps = {
  title: string;
  type: "ready" | "progress" | "upgrade" | "expired";
  receivedDate: Date;
  expirationDate?: Date;
  applicantId: string;
  canDeclineApplication: boolean | null;
  onPress: () => void;
  teamName?: string | null;
};

const config: Record<
  CardProps["type"],
  {
    testId: string;
    background: CascadiaColor;
    statusText: React.ReactElement;
    actionBarBackground: CascadiaColor;
    actionBarIconColor: CascadiaColor;
    textColor: CascadiaColor;
    receivedText: React.ReactElement;
    button: (onPress: () => void) => React.ReactElement;
  }
> = {
  ready: {
    testId: "check-card-get-started-badge",
    background: "background-success-strong",
    statusText: <Trans>Ready to start</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-success",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="dashboard-check-card-get-started-button"
        size="medium"
        fullWidth
        onPress={onPress}
      >
        <Trans>Get started</Trans>
      </PrimaryButton>
    ),
  },
  upgrade: {
    testId: "check-card-upgrade-badge",
    background: "background-success-strong",
    statusText: <Trans>Ready to start</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-success",
    textColor: "text-weak",
    receivedText: <Trans>Follow up check requested</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="dashboard-check-card-get-started-button"
        size="medium"
        fullWidth
        onPress={onPress}
      >
        <Trans>Get started</Trans>
      </PrimaryButton>
    ),
  },
  progress: {
    testId: "dashboard-check-card-in-progress-badge",
    background: "background-info-strong",
    statusText: <Trans>In progress</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-info",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="check-card-continue-button"
        size="medium"
        variant="info"
        fullWidth
        onPress={onPress}
      >
        <Trans>Continue</Trans>
      </PrimaryButton>
    ),
  },
  expired: {
    testId: "dashboard-check-card-expired-badge",
    background: "background-inverse",
    statusText: <Trans>Expired</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "background-inverse",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <GhostButton
        border="border-strong"
        size="medium"
        fullWidth
        testID="dashboard-check-card-archive-button"
        onPress={onPress}
      >
        <Trans>Archive</Trans>
      </GhostButton>
    ),
  },
};

export function PortalCard({
  title,
  type,
  onPress,
  receivedDate,
  canDeclineApplication,
  expirationDate,
  applicantId,
  teamName,
}: CardProps) {
  const styles = useCardStyles();
  const { t } = useTranslation();
  const expiredTimeAgo = usePastTimeDifference(expirationDate ?? new Date());
  const { days: daysToExpire } = useFutureTimeDifference(
    expirationDate ?? new Date()
  );
  const receivedTimeAgo = usePastTimeDifference(receivedDate);
  const isExpired = expirationDate && expirationDate.getTime() < Date.now();
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const colors = useColors();

  return (
    <>
      <Card
        type="large"
        border="border-base"
        background="background-accent-neutral-weak"
        className={styles.card}
      >
        {expirationDate && !isExpired && daysToExpire <= 7 && (
          <ExpirationBar expirationDate={expirationDate} />
        )}
        <div className={styles.statusDecline}>
          <CheckStatus type={type} testId={config[type].testId}>
            {config[type].statusText}{" "}
            {type === "expired" ? `${expiredTimeAgo}` : undefined}
          </CheckStatus>
          {canDeclineApplication && (
            <div className={css({ marginLeft: "auto" })}>
              <LegacyDropdownMenu
                triggerButton={
                  <GhostIconButton
                    testID="dashboard-expand-portal-card-button"
                    size="small"
                    icon="more_horiz"
                    aria-label={t("Expand menu")}
                  />
                }
              >
                <LegacyMenuSection>
                  <LegacyMenuItem
                    itemKey="decline_request"
                    onClick={() => setShowDeclineModal(true)}
                  >
                    <Text
                      style="input/text-field"
                      testID="dashboard-decline-request-button"
                    >
                      <Trans>Decline request</Trans>
                    </Text>
                  </LegacyMenuItem>
                </LegacyMenuSection>
              </LegacyDropdownMenu>
            </div>
          )}
        </div>
        <Text as="h3" style="global/body-secondary">
          <Trans>Background check application</Trans>
        </Text>
        <Text style="card/title-large">{title}</Text>
        <div
          className={css(
            styles.actionBar,
            css({ background: colors[config[type].actionBarBackground] })
          )}
        >
          <div className={styles.actionBarText}>
            <Icon
              name="inbox"
              color={config[type].actionBarIconColor}
              type="standard"
              padding="space-1-5"
            />
            <Text style="input/label" color={config[type].textColor}>
              {config[type].receivedText} {receivedTimeAgo}
            </Text>
          </div>
          <div className={styles.buttonContainer}>
            {config[type].button(onPress)}
          </div>
        </div>
        <div
          className={css(
            styles.background,
            css({
              background: colors[config[type].background],
            })
          )}
        />
        <div className={styles.circles}>
          <span className={css(styles.circle, styles.c1)} />
          <span className={css(styles.circle, styles.c2)} />
          <span className={css(styles.circle, styles.c3)} />
          <span className={css(styles.circle, styles.c4)} />
          <span className={css(styles.circle, styles.c5)} />
          <span className={css(styles.circle, styles.c6)} />
        </div>
      </Card>
      <DeclineModal
        show={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        applicantId={applicantId}
        teamName={teamName}
      />
    </>
  );
}
